'use client';

import { type FC, useState } from 'react';

import { ChevronDown } from '@gds/Icons/Paths/ChevronDown';
import { Typography } from '@gds/Typography/Typography';

import type { FooterGroup } from '../Footer/Footer.entity';
import {
  itemOnClick,
  TrackedFooterLink,
  trackingFunction,
} from '../TrackedFooterLink/TrackedFooterLink';

import styles from './FooterLinks.module.css';

export const FooterLinks: FC<{
  columns: FooterGroup[] | undefined;
}> = ({ columns }) => {
  const [expandedGroup, setExpandedGroup] = useState<Record<string, boolean>>({
    'group0-0': true,
  });

  const toggleMenu = (group: string, heading: string) => {
    itemOnClick({
      fn: trackingFunction,
      obj: { label: heading },
    });
    setExpandedGroup(expandedGroup => ({
      ...expandedGroup,
      [group]: !expandedGroup[group],
    }));
  };

  return (
    <>
      {columns?.map(({ footerLinksGroup }, columnIndex) => (
        <div className={styles.groups} key={columnIndex}>
          {footerLinksGroup?.map(({ heading, footerLinkItems }, groupIndex) => (
            <div className={styles.group} key={groupIndex}>
              <button
                data-expanded={expandedGroup[`group${columnIndex}-${groupIndex}`] ?? false}
                onClick={() => toggleMenu(`group${columnIndex}-${groupIndex}`, heading)}
              >
                <Typography Component="span" variant="body2">
                  {heading}
                </Typography>
                {/* Decorative icon; hide from accessibility tree to avoid affecting button name */}
                <ChevronDown fontSize={16} aria-hidden />
              </button>
              <ul
                data-expanded={expandedGroup[`group${columnIndex}-${groupIndex}`] ?? false}
                className={styles.menuList}
                data-test-id="footer-group-links"
              >
                {footerLinkItems?.map(({ label, href, footerLinkTarget }) => (
                  <li className={styles.menuListItem} key={label}>
                    {href ? (
                      <TrackedFooterLink
                        prefetch={false}
                        href={href}
                        trackingObj={{ category: 'footer_navigation', label, href }}
                        {...(footerLinkTarget?.length && { target: '_blank' })}
                      >
                        <Typography Component="span" variant="body4">
                          {label}
                        </Typography>
                      </TrackedFooterLink>
                    ) : (
                      <Typography Component="span" variant="body4">
                        {label}
                      </Typography>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ))}
    </>
  );
};
